import { useSubscribe } from "~/features/push-notifications/use-subscribe";
import { fetchPushSubscription } from "~/features/push-notifications/utils";
import { sharedLocalStorage } from "~/features/storage/local.client";
import { useHashParams } from "~/hooks/use-hash-params";
import { useHydrated } from "~/hooks/use-hydrated";

import { datadogRum } from "@datadog/browser-rum";
import { useQuery } from "@tanstack/react-query";

export const DEVICE_TOKEN_QUERY_KEY = "deviceToken";

export const useNativeSubscription = (
  employeeId: string,
  paramKey = "deviceToken",
) => {
  const [hashParams, setHashParams] = useHashParams();
  const deviceTokenFromParams = hashParams.get(paramKey);
  const subscribe = useSubscribe();

  const isHydrated = useHydrated();

  // Get the currently stored device token from IndexedDB
  const localDeviceToken = useQuery({
    enabled: isHydrated,
    staleTime: 0,
    queryKey: [DEVICE_TOKEN_QUERY_KEY, "local"] as const,
    networkMode: "always",
    queryFn: () => sharedLocalStorage.deviceToken.get(),
  });

  /**
   * Add a native subscription, and, if it is successful, store it in IndexedDB and clear it from the hash params.
   *
   * @param deviceToken - The device token to subscribe.
   */
  function addSubscription(deviceToken: string) {
    return subscribe.mutate(
      { employeeId, deviceToken },
      {
        onSuccess() {
          void sharedLocalStorage.deviceToken.set(deviceToken);
          setHashParams((prev) => {
            prev.delete(paramKey);
            return prev;
          });
        },
      },
    );
  }

  // Use the local device token and the device token from the hash params to determine if we need to add a subscription
  // If they are the same, we check that the subscription exists in the database.
  useQuery({
    notifyOnChangeProps: [],
    queryKey: [
      "deviceToken",
      "subscription",
      localDeviceToken.data,
      deviceTokenFromParams,
      employeeId,
    ] as const,
    enabled:
      localDeviceToken.isFetched &&
      (!!deviceTokenFromParams || !!localDeviceToken.data),
    async queryFn({ queryKey: [, , local, fromParams, employeeId] }) {
      const isNewToken = !!fromParams && fromParams !== local;
      const shouldCheckExistingSub =
        !!local && (!fromParams || fromParams === local);

      if (isNewToken) {
        datadogRum.addAction("twa.device_token_received_via_redirect");
        addSubscription(fromParams);
        return true;
      } else if (shouldCheckExistingSub) {
        const sub = await fetchPushSubscription(
          {
            employeeId,
            action: "read",
            subscription: {
              registrationToken: local,
            },
          },
          "POST",
        );

        if (sub.ok) {
          setHashParams((prev) => {
            prev.delete(paramKey);
            return prev;
          });
          return true;
        }

        if ("status" in sub && sub.status === 404) {
          datadogRum.addAction(
            "twa.device_token_subscription_missing_resubscribe",
          );
          addSubscription(local);
          return true;
        } else {
          throw new Error("Failed to check existing subscription");
        }
      }

      return true;
    },
  });
};
